import { useState, type RefObject } from "react";
import { useScheme, screenTitleOverride } from "../schemeContext";
import GuideScreen from "../guide/GuideScreen";
import VodScreen, { type VodScreenStyle } from "../vod/VodScreen";
import TvHome from "./TvHome";
import TvShell, { type TvSection } from "./TvShell";

/**
 * Foyer's own television: the menu, and everything reachable from it.
 *
 * ## 🚨 Why this is not part of the launcher kit
 *
 * It used to be, as `LauncherTv`, and that is exactly how the five launcher
 * templates and the three bespoke ones came to disagree about what the TV tile
 * DOES. `cbbaf60` wired atelier, mosaic and spectrum to open the guide
 * directly; fifteen minutes later `1fd8e91` introduced this shell and gave the
 * reason in its own message:
 *
 * > Until now the built-in branch dropped a guest straight into the guide.
 * > That is a channel list, not a television: landing in one leaf of a
 * > structure with no way to reach the others reads as a broken app rather
 * > than a smaller one.
 *
 * Only the kit was migrated. The three bespoke templates kept the superseded
 * behaviour for a week — one button meaning two things, which nobody asked
 * for. Lifting the player out of the kit is what lets all eight share it: the
 * only thing it ever needed from a `LauncherTheme` was a `VodScreenStyle`,
 * which every template already builds for `VodScreen`.
 *
 * ## What it draws
 *
 * A left icon rail (`TvShell`) over sections that exist only when they hold
 * something, plus a full-screen channel overlay when a guest tunes one.
 */
export interface BuiltInPlayerProps {
  /** The template's own palette — the same object it hands `VodScreen`. */
  style: VodScreenStyle;
  /** Leave the player. Back from the rail, the level that owns leaving. */
  onExit: () => void;
  scopeRef: RefObject<HTMLElement>;
}

export default function BuiltInPlayer({
  style,
  onExit,
  scopeRef,
}: BuiltInPlayerProps) {
  const scheme = useScheme();
  /**
   * The channel the guest is watching, or null for the menu.
   *
   * 🚨 This is what OK on the home carousel does, and until TV-9 it did
   * NOTHING. `TvHome` calls `onTune?.(channel)` and this file mounted it with
   * `onOpen` only, so a channel card drew a focus ring, answered the arrows and
   * ignored OK - which on a television is indistinguishable from a freeze. The
   * launcher family (aurelia, marea, nord, terra, tann) therefore had no way to
   * reach live TV at all, on a page whose first row is channels.
   *
   * Watching is an OVERLAY rather than a menu section, deliberately, and both
   * halves of that matter:
   *
   *  - Not a section, because the rail's own comment argues correctly that a
   *    "Kanaler" entry beside a home page that already carries the channel
   *    carousel is a second place to look for one thing. That reasoning was
   *    about the MENU, not about whether OK works.
   *  - Not a hidden section either: `TvShell` couples the focus row to the
   *    section index ("the grid's own row IS which section is shown"), so a row
   *    the rail does not draw is a row the remote can still land on. Fixing
   *    that coupling to serve this is a bigger change than the bug deserves.
   *
   * So it takes the whole panel, exactly as pressing a channel on a television
   * does, and Back returns to the menu with the carousel where it was.
   */
  const [watching, setWatching] = useState<string | null>(null);
  // OPAQUE, unlike every other sub-page. `launcherPlayerStyle` grounds the
  // inset pages on `transparent` so the template's photograph shows through -
  // right for a page inside the chrome, wrong for this one, which replaces it.
  // Left transparent, each template's own top chrome (its hotel name, its
  // clock) kept drawing straight through the player, so the property's name
  // appeared twice: once across the top and once in the menu.
  const sections: TvSection[] = [];
  /**
   * 🚨 Truthiness is NOT the test. An answered-but-empty feed is a real object
   * — `{rails: [], subPages: []}`, or a line-up with no tunable channel — and
   * Foyer production holds zero `packages` rows, so "answered with
   * nothing" is the COMMON case, not an edge one. Gating on the object alone
   * put a "Film & Serier" row in the menu that opened the empty shelf this
   * file's own contract says a guest must never be shown. The contract is
   * "undefined means not answered, empty means nothing to sell" — and only
   * content, never mere arrival, earns a menu row.
   */
  const hasVod = (scheme.vod?.rails ?? []).some((r) => r.items.length > 0);
  const hasGuide = (scheme.guide?.channels ?? []).length > 0;
  // Home first, and only when there is something under it. It draws the
  // channel carousel and the premise's own rails, so a room with neither has
  // no home to show and the menu starts on whichever section it does have.
  if (hasGuide || hasVod) {
    sections.push({
      id: "home",
      label: "Hem",
      icon: "tv",
      render: ({ scopeRef: ref, onBack, goTo }) => (
        <TvHome
          scopeRef={ref}
          onBack={onBack}
          style={style}
          // OK on a poster lands on the page that can actually play it.
          // Without this the home page answered arrows but not OK, which is
          // indistinguishable from a frozen television.
          onOpen={() => goTo("vod")}
          // ...and OK on a channel starts watching it. Same failure, same fix,
          // one row higher up the page. See `watching` above.
          onTune={(ch) => setWatching(ch.id)}
        />
      ),
    });
  }
  // No "Kanaler" section. The home page already carries the channel carousel,
  // and a menu entry that opens a second, list-shaped view of the same channels
  // is a second place to look for one thing. The `guide` SCREEN still exists and
  // is still placeable by an operator - it is just not part of this menu.
  if (hasVod) {
    sections.push({
      id: "vod",
      label: screenTitleOverride(scheme, "vod") || "Film & Serier",
      icon: "vod",
      render: ({ scopeRef: ref, onBack }) => (
        <VodScreen
          scopeRef={ref}
          onBack={onBack}
          pad={{ x: 48, y: 32 }}
          hints={false}
          style={style}
        />
      ),
    });
  }
  return (
    <div
      ref={scopeRef as RefObject<HTMLDivElement>}
      style={{
        position: "absolute",
        inset: 0,
        // Above the template's top chrome, which sits at zIndex 2 and is
        // rendered OUTSIDE the screen branch by every launcher template.
        zIndex: 5,
        background: style.bg,
      }}
    >
      {watching ? (
        <GuideScreen
          scopeRef={scopeRef}
          style={style}
          startChannelId={watching}
          // Back leaves the CHANNEL, not the player: the guest came from the
          // menu and that is where they expect to return. Exiting the built-in
          // player entirely is still one more Back, from the rail, which is the
          // level that owns it.
          onBack={() => setWatching(null)}
          pad={{ x: 0, y: 0 }}
        />
      ) : (
        <TvShell
          style={style}
          hotelName={scheme.context.hotelName}
          sections={sections}
          // The server's verdict on an empty room, carried rather than
          // re-derived — `sections.length === 0` says THAT there is nothing,
          // and only this says why.
          emptyState={scheme.vod?.emptyState}
          diagnose={scheme.context.preview}
          onExit={onExit}
        />
      )}
    </div>
  );
}
